import React from 'react';
import AdminRowIcon from '../../components/admin/AdminRowIcon';
import { timeOutline, waterOutline } from 'ionicons/icons';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonList,
  IonItem,
  IonBadge,
  IonSpinner,
  IonText,
  IonSearchbar,
} from '@ionic/react';
import { useAdminIrrigation } from '../../../application/use-cases/admin/useAdminIrrigation';
import { DAY_LABELS, skipReasonLabel } from '../../../domain/Irrigation';
import AdminListFooter from '../../components/admin/AdminListFooter';
import './Admin.css';

const AdminIrrigation: React.FC = () => {
  const { tab, setTab, schedules, events, loading, error } = useAdminIrrigation();

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/admin" />
          </IonButtons>
          <IonTitle>Riego</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={tab} onIonChange={(e) => setTab(e.detail.value as 'configured' | 'events')}>
            <IonSegmentButton value="configured">
              <IonLabel>Horarios ({schedules.total.toLocaleString('es-AR')})</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="events">
              <IonLabel>Historial ({events.total.toLocaleString('es-AR')})</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
        <IonToolbar>
          {tab === 'configured' ? (
            <IonSearchbar
              value={schedules.query}
              placeholder="Buscar por estación o dueño"
              onIonInput={(e) => schedules.setQuery(e.detail.value ?? '')}
            />
          ) : (
            <IonSearchbar
              value={events.query}
              placeholder="Buscar por estación, dueño o estado"
              onIonInput={(e) => events.setQuery(e.detail.value ?? '')}
            />
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        )}
        {error && (
          <IonText color="danger">
            <p className="ion-padding">{error}</p>
          </IonText>
        )}

        {!loading && tab === 'configured' && (
          <IonList className="admin-list">
            {schedules.items.map((s) => (
              <IonItem key={s.id} lines="full">
                <AdminRowIcon icon={timeOutline} tone={s.deletedAt || !s.enabled ? 'grey' : 'blue'} />
                <IonLabel>
                  <h2>
                    {s.deviceName} · Sector {s.valveChannel}
                  </h2>
                  <p>
                    {s.ownerName} ({s.ownerEmail})
                  </p>
                  <p className="admin-list-row-meta">
                    {s.daysOfWeek
                      .slice()
                      .sort()
                      .map((d) => DAY_LABELS[d])
                      .join(', ')}{' '}
                    · {s.startTime} · {Math.round(s.durationSeconds / 60)} min · {s.flowRateLpm} L/min
                    {s.humidityDeviceId && ' · con sonda de suelo'}
                    {s.notifyOnSkip && ' · avisa al saltear'}
                  </p>
                </IonLabel>
                {s.deletedAt && <IonBadge color="medium">Eliminado</IonBadge>}
                {!s.deletedAt && !s.enabled && <IonBadge color="medium">Pausado</IonBadge>}
                {!s.deletedAt && s.enabled && <IonBadge color="success">Activo</IonBadge>}
              </IonItem>
            ))}
            {!loading && schedules.items.length === 0 && (
              <IonItem lines="none">
                <IonLabel color="medium">
                  {schedules.query ? 'Ningún horario coincide con la búsqueda.' : 'Todavía no hay ningún horario de riego configurado.'}
                </IonLabel>
              </IonItem>
            )}
          </IonList>
        )}
        {!loading && tab === 'configured' && (
          <AdminListFooter shown={schedules.items.length} total={schedules.total} loadingMore={schedules.loadingMore} onMore={schedules.loadMore} />
        )}

        {!loading && tab === 'events' && (
          <IonList className="admin-list">
            {events.items.map((e) => (
              <IonItem key={e.id} lines="full">
                <AdminRowIcon icon={waterOutline} tone="blue" />
                <IonLabel>
                  <h2>
                    {e.deviceName} · Sector {e.valveChannel}
                  </h2>
                  <p>{e.ownerEmail}</p>
                  <p className="admin-list-row-meta">
                    {e.status === 'skipped'
                      ? e.skipReason
                        ? skipReasonLabel[e.skipReason]
                        : 'Salteado'
                      : `${e.triggerSource === 'manual' ? 'Riego manual' : 'Riego programado'}${
                          e.estimatedLiters !== null ? ` · ${e.estimatedLiters} L` : ''
                        }`}
                  </p>
                  <p className="admin-list-row-meta">
                    {e.startedAt ? new Date(e.startedAt).toLocaleString('es-AR') : ''}
                    {e.status === 'running' && ' · en curso'}
                  </p>
                </IonLabel>
              </IonItem>
            ))}
            {!loading && events.items.length === 0 && (
              <IonItem lines="none">
                <IonLabel color="medium">
                  {events.query ? 'Ningún riego coincide con la búsqueda.' : 'Todavía no hay riegos registrados en el sistema.'}
                </IonLabel>
              </IonItem>
            )}
          </IonList>
        )}
        {!loading && tab === 'events' && (
          <AdminListFooter shown={events.items.length} total={events.total} loadingMore={events.loadingMore} onMore={events.loadMore} />
        )}
      </IonContent>
    </IonPage>
  );
};

export default AdminIrrigation;
